import { ActionCreator } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { BudgetCostsApi } from '../../../api/budgetCostsApi';
import { toastMsg } from '../../../components/Toast/toastMsg';
import { ProjectsState } from '../reducers';
import { IProjectBudgetCost, IProjectBudgetCostChart } from '../types/costs';
import { IPagination } from '../../../types/pagination';
import * as constants from './constants';


export type BudgetCostsThunkResult<R> = ThunkAction<Promise<R>, ProjectsState, null, BudgetCostsActions>
export type BudgetCostsThunkDispatchType = ThunkDispatch<ProjectsState, null, BudgetCostsActions>


export interface GetBudgetCostsSuccess {
  type: constants.GET_COSTS_SUCCESS
  costs: IProjectBudgetCost[]
}

export interface GetOutstandingPaymentsSuccess {
  type: constants.GET_COSTS_OUTSTANDING_SUCCESS
  costs: IProjectBudgetCost[]
}

export interface GetPaginatedOutstandingPaymentsSuccess {
  type: constants.GET_PAGINATED_COSTS_OUTSTANDING_SUCCESS
  costs: IPagination<IProjectBudgetCost[]>
}

export interface SetPaginatedOutstandingPaymentsActivePage {
  type: constants.SET_PAGINATED_COSTS_OUTSTANDING_ACTIVE_PAGE
  page: number
}

export interface GetBudgetCostSuccess {
  type: constants.GET_COST_SUCCESS
  cost: IProjectBudgetCost
}

export interface UpdateBudgetCostSuccess {
  type: constants.UPDATE_COST_SUCCESS
  cost: IProjectBudgetCost
}

export interface CreateBudgetCostSuccess {
  type: constants.CREATE_COST_SUCCESS
  cost: IProjectBudgetCost
}

export interface DeleteBudgetCostSuccess {
  type: constants.DELETE_COST_SUCCESS
  cost: IProjectBudgetCost
}

export interface GetBudgetCostsErrored {
  type: constants.COSTS_HAS_ERRORED,
  hasErrored: boolean
}

export interface GetBudgetCostsChartSuccess {
  type: constants.GET_COSTS_CHART_SUCCESS
  costs: IProjectBudgetCostChart
}

export const getBudgetCostsHasErrored = (hasErrored: boolean, error: Error, action?: string): GetBudgetCostsErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.COSTS_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const getBudgetCosts: ActionCreator<BudgetCostsThunkResult<IProjectBudgetCost[]>>
  = (projectId: number | string, queryParams: Record<string, string>): BudgetCostsThunkResult<IProjectBudgetCost[]> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.getProjectCosts(projectId, queryParams)
        .then(response => {
          dispatch(getBudgetCostsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.GET_COSTS))
          return {} as IProjectBudgetCost[]
        })
    }
  }


export const getBudgetCostsSuccess = (costs: IProjectBudgetCost[]): GetBudgetCostsSuccess => {
  return {
    type: constants.GET_COSTS_SUCCESS,
    costs
  }
}


export const getOutstandingPayments: ActionCreator<BudgetCostsThunkResult<IProjectBudgetCost[]>>
  = (projectId: number | string, queryParams: Record<string, string>): BudgetCostsThunkResult<IProjectBudgetCost[]> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.getOutstandingPayments(projectId, queryParams)
        .then(response => {
          dispatch(getOutstandingPaymentsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.GET_COSTS_OUTSTANDING))
          return {} as IProjectBudgetCost[]
        })
    }
  }


export const getOutstandingPaymentsSuccess = (costs: IProjectBudgetCost[]): GetOutstandingPaymentsSuccess => {
  return {
    type: constants.GET_COSTS_OUTSTANDING_SUCCESS,
    costs
  }
}

export const getPaginatedOutstandingPayments: ActionCreator<BudgetCostsThunkResult<IPagination<IProjectBudgetCost[]>>>
  = (projectId: number | string, queryParams: Record<string, string>): BudgetCostsThunkResult<IPagination<IProjectBudgetCost[]>> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.getPaginatedOutstandingPayments(projectId, queryParams)
        .then(response => {
          dispatch(getPaginatedOutstandingPaymentsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.GET_PAGINATED_COSTS_OUTSTANDING))
          return {} as IPagination<IProjectBudgetCost[]>
        })
    }
  }


export const getPaginatedOutstandingPaymentsSuccess = (costs: IPagination<IProjectBudgetCost[]>): GetPaginatedOutstandingPaymentsSuccess => {
  return {
    type: constants.GET_PAGINATED_COSTS_OUTSTANDING_SUCCESS,
    costs
  }
}

export const setPaginatedOutstandingActivePage = (page: number): SetPaginatedOutstandingPaymentsActivePage => {
  return {
    type: constants.SET_PAGINATED_COSTS_OUTSTANDING_ACTIVE_PAGE,
    page: page
  }
}

export const getBudgetCostsChart: ActionCreator<BudgetCostsThunkResult<IProjectBudgetCostChart>>
  = (projectId: number | string, queryParams: Record<string, string>): BudgetCostsThunkResult<IProjectBudgetCostChart> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.getProjectCostsBudgetChart(projectId, queryParams)
        .then(response => {
          dispatch(getBudgetCostsChartSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.GET_COSTS_CHART))
          return {} as IProjectBudgetCostChart
        })
    }
  }


export const getBudgetCostsChartSuccess = (costs: IProjectBudgetCostChart): GetBudgetCostsChartSuccess => {
  return {
    type: constants.GET_COSTS_CHART_SUCCESS,
    costs
  }
}

export const getBudgetCost: ActionCreator<BudgetCostsThunkResult<IProjectBudgetCost>>
  = (projectId: string | number, cost: IProjectBudgetCost): BudgetCostsThunkResult<IProjectBudgetCost> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.getProjectCost(projectId, cost)
        .then(response => {
          dispatch(getBudgetCostSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.GET_COST))
          return {} as IProjectBudgetCost
        })
    }
  }

export const getBudgetCostSuccess = (cost: IProjectBudgetCost): GetBudgetCostSuccess => {
  return {
    type: constants.GET_COST_SUCCESS,
    cost
  }
}

export const createBudgetCost: ActionCreator<BudgetCostsThunkResult<IProjectBudgetCost>>
  = (projectId: string | number, cost: IProjectBudgetCost): BudgetCostsThunkResult<IProjectBudgetCost> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.createProjectCost(projectId, cost)
        .then(response => {
          dispatch(createBudgetCostSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.CREATE_COST))
          return {} as IProjectBudgetCost
        })
    }
  }

export const createBudgetCostSuccess = (cost: IProjectBudgetCost): CreateBudgetCostSuccess => {
  toastMsg(`Create ${cost.position} successfully.`, constants.CREATE_COST_SUCCESS, 'success')
  return {
    type: constants.CREATE_COST_SUCCESS,
    cost
  }
}

export const updateBudgetCost: ActionCreator<BudgetCostsThunkResult<IProjectBudgetCost>>
  = (projectId: string | number, cost: IProjectBudgetCost): BudgetCostsThunkResult<IProjectBudgetCost> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.updateProjectCost(projectId, cost)
        .then(response => {
          dispatch(updateBudgetCostSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.UPDATE_COST))
          return {} as IProjectBudgetCost
        })
    }
  }

export const updateBudgetCostSuccess = (cost: IProjectBudgetCost): UpdateBudgetCostSuccess => {
  toastMsg(`Update ${cost.position} successfully.`, constants.UPDATE_COST_SUCCESS, 'success')
  return {
    type: constants.UPDATE_COST_SUCCESS,
    cost
  }
}

export const deleteBudgetCost: ActionCreator<BudgetCostsThunkResult<string | number | void>>
  = (projectId: string | number, cost: IProjectBudgetCost): BudgetCostsThunkResult<string | number | void> => {
    return async (dispatch: BudgetCostsThunkDispatchType) => {
      return BudgetCostsApi.deleteProjectCost(projectId, cost)
        .then(response => {
          dispatch(deleteBudgetCostSuccess(cost))
          return response
        })
        .catch(error => {
          dispatch(getBudgetCostsHasErrored(true, error.response.data, constants.DELETE_COST))
        })
    }
  }

export const deleteBudgetCostSuccess = (cost: IProjectBudgetCost): DeleteBudgetCostSuccess => {
  toastMsg(`Deleted ${cost.position} successfully.`, constants.DELETE_COST_SUCCESS, 'success')
  return {
    type: constants.DELETE_COST_SUCCESS,
    cost
  }
}

export type BudgetCostsActions
  = GetBudgetCostsSuccess
  | GetBudgetCostSuccess
  | CreateBudgetCostSuccess
  | UpdateBudgetCostSuccess
  | DeleteBudgetCostSuccess
  | GetBudgetCostsChartSuccess
  | GetBudgetCostsErrored
  | GetOutstandingPaymentsSuccess
  | GetPaginatedOutstandingPaymentsSuccess
  | SetPaginatedOutstandingPaymentsActivePage
